<template>
	<div>
		<div class="tw-mb-6">
			<div class="tw-flex tw-flex-col lg:tw-flex-row tw-gap-4">
				<div class="tw-flex-1">
					<button 
						@click="resetSilo"
						:class="theme === 'dark' ? 'tw-bg-emerald-600 hover:tw-bg-emerald-700' : 'tw-bg-emerald-500 hover:tw-bg-emerald-600'"
						class="tw-w-full tw-px-6 tw-py-3 tw-rounded-xl tw-text-white tw-font-semibold"
					>
						Voltar modelo 3D à posição inicial
					</button>
				</div>
				<div :class="theme === 'dark' ? 'tw-bg-slate-800' : 'tw-bg-white'" class="tw-rounded-xl tw-p-4 tw-max-h-80 tw-overflow-y-auto tw-flex-1 lg:tw-max-w-xs">
					<div class="tw-flex tw-items-center tw-gap-2 tw-mb-4">
						<span :class="theme === 'dark' ? 'tw-text-slate-300' : 'tw-text-slate-700'" class="tw-text-sm tw-font-semibold">Elementos Visíveis</span>
					</div>
					
					<div class="tw-space-y-3">
						<div v-for="(arco, index) in arcosExistentes" :key="index" class="tw-flex tw-items-center">
							<label class="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer">
								<input 
									type="checkbox" 
									:id="'arco-' + arco" 
									v-model="arcosVisiveis[arco]" 
									@change="toggleArco(arco)"
									class="tw-w-4 tw-h-4 tw-rounded"
								>
								<span :class="theme === 'dark' ? 'tw-text-slate-300' : 'tw-text-slate-700'" class="tw-text-sm tw-font-medium">
									Arco {{ arco }}
								</span>
							</label>
						</div>

						<div v-if="possuiCaboCentral" class="tw-flex tw-items-center tw-pt-2 tw-border-t" :class="theme === 'dark' ? 'tw-border-slate-700' : 'tw-border-slate-200'">
							<label class="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer">
								<input
									type="checkbox"
									id="cabo-central"
									v-model="caboCentralVisivel"
									@change="toggleCaboCentral"
									class="tw-w-4 tw-h-4 tw-rounded"
								>
								<span :class="theme === 'dark' ? 'tw-text-slate-300' : 'tw-text-slate-700'" class="tw-text-sm tw-font-medium">
									Cabo Central
								</span>
							</label>
						</div>

						<div v-if="silo.tipo === 'silo'" class="tw-flex tw-items-center tw-pt-2 tw-border-t" :class="theme === 'dark' ? 'tw-border-slate-700' : 'tw-border-slate-200'">
							<label class="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer">
								<input
									type="checkbox"
									id="nivel"
									v-model="nivelVisivel"
									@change="toggleNivel"
									class="tw-w-4 tw-h-4 tw-rounded"
								>
								<span :class="theme === 'dark' ? 'tw-text-slate-300' : 'tw-text-slate-700'" class="tw-text-sm tw-font-medium">
									Nível
								</span>
							</label>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="tw-relative tw-rounded-2xl tw-overflow-hidden tw-shadow-lg tw-w-full" :style="wrapperStyle">
			<canvas id="scene" ref="scene"></canvas>
			<div
				class="tw-absolute tw-left-3 tw-bottom-3 tw-text-xs tw-px-2.5 tw-py-1.5 tw-rounded-lg tw-border tw-pointer-events-none"
				:class="theme === 'dark' ? 'tw-bg-slate-800/80 tw-text-slate-400 tw-border-slate-700' : 'tw-bg-white/80 tw-text-slate-500 tw-border-slate-200'"
			>
				Arraste para girar · scroll para aproximar · botão direito para mover
			</div>
		</div>
	</div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { useToast } from 'vue-toastification';
import temperaturaCores from "@/assets/colors.json";

// Mapa nível (%) -> altura visual do grão dentro do silo.
const NIVEL_MAP = [
	{ max: 5, nivel: 0.04 },
	{ max: 11, nivel: 0.05 },
	{ max: 21, nivel: 0.2 },
	{ max: 30, nivel: 0.5 },
	{ max: 41, nivel: 0.7 },
	{ max: 51, nivel: 1 },
	{ max: 61, nivel: 1.2 },
	{ max: 71, nivel: 1.4 },
	{ max: 81, nivel: 1.6 },
	{ max: 91, nivel: 1.8 },
	{ max: 101, nivel: 2 }
];

// Raio do silo.
const R = 1.7;
// Raio (posição radial) dos cabos por quantidade de arcos.
const RAIO_ARCO_MAP = {
	1: [R * 0.5],
	2: [R * 0.8, R * 0.4],
	3: [R * 0.75, R * 0.5, R * 0.25],
	4: [R * 0.8, R * 0.6, R * 0.4, R * 0.2],
	5: [R * 0.831, R * 0.653, R * 0.499, R * 0.333, R * 0.166]
};

// Cores do grão (gradiente vertical).
const GRAO_BAIXO = '#a9741a';
const GRAO_TOPO = '#e8c65a';
// Margem entre o primeiro/último sensor e as pontas do cabo.
const MARGEM_CABO = 0.12;
// Cor do número do cabo.
const COR_CABO = '#38bdf8';

export default {
	name: 'Silo3dComponent',
	props: {
		termometria: {
			type: Object,
			default: () => ({}),
		},
		silo: {
			type: Object,
			default: () => ({}),
		},
		theme: {
			type: String,
			default: 'light'
		}
	},
	data() {
		return {
			arcosVisiveis: {},
			arcosExistentes: [],
			caboCentralVisivel: true,
			possuiCaboCentral: false,
			nivelVisivel: true
		};
	},
	computed: {
		wrapperStyle() {
			return this.theme === 'dark'
				? { background: 'radial-gradient(circle at 50% 28%, #232a35 0%, #12161d 68%, #0b0e13 100%)' }
				: { background: 'radial-gradient(circle at 50% 28%, #f8fafc 0%, #dbe3ec 62%, #c3cdd9 100%)' };
		}
	},
	watch: {
		silo: {
			handler() { this.scheduleRebuild(); },
			deep: true
		},
		termometria: {
			handler() { this.scheduleRebuild(); },
			deep: true
		}
	},
	mounted() {
		const toast = useToast();
		try {
			this.computeArcos();
			this.initThreeJS();
		} catch (e) {
			toast.error('Erro ao gerar silo 3D');
			console.error(e);
		}
	},
	beforeUnmount() {
		if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
		if (this._resizeObserver) this._resizeObserver.disconnect();
		if (this._frameId) cancelAnimationFrame(this._frameId);
		this.disposeSilo();
		(this._sceneDisposables || []).forEach(d => { try { d.dispose && d.dispose(); } catch (_) { /* noop */ } });
		if (this.controls) this.controls.dispose();
		if (this.renderer) {
			this.renderer.dispose();
			if (this.renderer.forceContextLoss) this.renderer.forceContextLoss();
		}
	},
	methods: {
		// ---------------------------------------------------------------
		// Dados
		// ---------------------------------------------------------------
		computeArcos() {
			const cabos = Array.isArray(this.silo?.cabos) ? this.silo.cabos : [];
			this.arcosExistentes = [...new Set(cabos.map(c => c.arco))].filter(a => a !== 999);
			const visiveis = {};
			this.arcosExistentes.forEach(a => { visiveis[a] = true; });
			this.arcosVisiveis = visiveis;
			this.possuiCaboCentral = cabos.some(c => c.arco === 999);
		},

		// ---------------------------------------------------------------
		// Setup da cena (uma vez)
		// ---------------------------------------------------------------
		initThreeJS() {
			const canvas = this.$refs.scene || document.getElementById('scene');
			const container = canvas.parentElement;
			const width = container.clientWidth || 800;
			const height = canvas.clientHeight || container.clientHeight || 600;

			this.scene = new THREE.Scene();

			this.camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
			this.camera.position.set(4.4, 2.5, 5.8);

			this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
			this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
			this.renderer.setSize(width, height, false);
			this.renderer.setClearColor(0x000000, 0);

			const hemi = new THREE.HemisphereLight(0xffffff, 0x4a5058, this.theme === 'dark' ? 0.95 : 1.05);
			this.scene.add(hemi);
			const key = new THREE.DirectionalLight(0xffffff, 1.1);
			key.position.set(5, 8, 6);
			this.scene.add(key);
			const fill = new THREE.DirectionalLight(0xbcd0e6, 0.35);
			fill.position.set(-6, 3, -4);
			this.scene.add(fill);

			this.buildContactShadow();

			this.controls = new OrbitControls(this.camera, this.renderer.domElement);
			this.controls.target.set(0, 0.15, 0);
			this.controls.enableDamping = false;
			this.controls.enableZoom = true;
			this.controls.minDistance = 3;
			this.controls.maxDistance = 17;
			this.controls.minPolarAngle = 0;
			this.controls.maxPolarAngle = Math.PI / 2.05;
			this.controls.update();
			this.controls.addEventListener('change', () => this.requestRender());

			this.buildSilo();

			this._resizeHandler = () => this.scheduleResize();
			window.addEventListener('resize', this._resizeHandler);
			if (typeof ResizeObserver !== 'undefined') {
				this._resizeObserver = new ResizeObserver(() => this.scheduleResize());
				this._resizeObserver.observe(canvas);
			}

			this.requestRender();
		},

		buildContactShadow() {
			const s = 256;
			const cv = document.createElement('canvas');
			cv.width = cv.height = s;
			const ctx = cv.getContext('2d');
			const grd = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
			grd.addColorStop(0, 'rgba(0,0,0,0.35)');
			grd.addColorStop(0.6, 'rgba(0,0,0,0.12)');
			grd.addColorStop(1, 'rgba(0,0,0,0)');
			ctx.fillStyle = grd;
			ctx.fillRect(0, 0, s, s);
			const tex = new THREE.CanvasTexture(cv);
			const geo = new THREE.PlaneGeometry(4.4, 4.4);
			const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false });
			const plane = new THREE.Mesh(geo, mat);
			plane.rotation.x = -Math.PI / 2;
			plane.position.y = -0.995;
			this.scene.add(plane);
			this._sceneDisposables = [tex, geo, mat];
		},

		// ---------------------------------------------------------------
		// Render sob demanda
		// ---------------------------------------------------------------
		requestRender() {
			if (!this.renderer || this._renderScheduled) return;
			this._renderScheduled = true;
			this._frameId = requestAnimationFrame(() => {
				this._renderScheduled = false;
				this.renderer.render(this.scene, this.camera);
			});
		},

		scheduleResize() {
			if (this._resizePending) return;
			this._resizePending = true;
			requestAnimationFrame(() => {
				this._resizePending = false;
				this.applyResize();
			});
		},

		applyResize() {
			const canvas = this.$refs.scene;
			if (!canvas || !this.renderer) return;
			// Mede a caixa real do canvas (após o layout) para manter o aspecto correto.
			const w = canvas.clientWidth || (canvas.parentElement && canvas.parentElement.clientWidth) || 800;
			const h = canvas.clientHeight || 600;
			if (!w || !h) return;
			this.camera.aspect = w / h;
			this.camera.updateProjectionMatrix();
			this.renderer.setSize(w, h, false);
			this.requestRender();
		},

		// ---------------------------------------------------------------
		// Build / rebuild
		// ---------------------------------------------------------------
		scheduleRebuild() {
			if (!this.renderer || this._rebuildQueued) return;
			this._rebuildQueued = true;
			this.$nextTick(() => {
				this._rebuildQueued = false;
				this.rebuild();
			});
		},

		rebuild() {
			if (!this.renderer) return;
			try {
				this.disposeSilo();
				this.computeArcos();
				this.buildSilo();
				this.requestRender();
			} catch (e) {
				console.error(e);
			}
		},

		track(resource) {
			this._disposables.push(resource);
			return resource;
		},

		buildSilo() {
			this._disposables = [];
			this._labelCache = new Map();
			this.arcoGroups = {};
			this.caboCentralGroup = null;
			this.nivelGroup = null;
			this.siloGroup = new THREE.Group();

			// Recursos compartilhados (descartados no rebuild).
			this._cableMat = this.track(new THREE.LineBasicMaterial({ color: 0x23272e }));
			this._circleTex = this.track(this.makeCircleTexture());
			this._beadMat = this.track(new THREE.PointsMaterial({
				map: this._circleTex, size: 0.17, sizeAttenuation: true, vertexColors: true,
				transparent: true, alphaTest: 0.5, depthTest: false, depthWrite: false
			}));
			this._shellMat = this.track(new THREE.MeshStandardMaterial({
				color: 0xb9c2cb, metalness: 0.18, roughness: 0.32,
				transparent: true, opacity: this.theme === 'dark' ? 0.3 : 0.26,
				side: THREE.DoubleSide, depthWrite: false
			}));
			this._roofMat = this.track(new THREE.MeshStandardMaterial({
				color: 0x9aa3ac, metalness: 0.22, roughness: 0.45, flatShading: true,
				transparent: true, opacity: this.theme === 'dark' ? 0.36 : 0.32,
				side: THREE.DoubleSide, depthWrite: false
			}));
			this._eaveMat = this.track(new THREE.MeshStandardMaterial({
				color: 0x8f99a3, metalness: 0.2, roughness: 0.45, transparent: true, opacity: 0.3, depthWrite: false
			}));
			this._bandMat = this.track(new THREE.MeshStandardMaterial({ color: 0x6b7580, metalness: 0.35, roughness: 0.5 }));
			this._ladderMat = this.track(new THREE.MeshStandardMaterial({ color: 0x6b7580, metalness: 0.3, roughness: 0.5, transparent: true, opacity: 0.92 }));
			this._floorMat = this.track(new THREE.MeshStandardMaterial({ color: 0x8a9098, metalness: 0.1, roughness: 0.9 }));
			this._lineMat = this.track(new THREE.LineBasicMaterial({
				color: this.theme === 'dark' ? 0x8b97a5 : 0x59636f, transparent: true, opacity: 0.5
			}));

			if (this.silo.tipo === 'silo') {
				this.buildSiloTank();
			} else {
				this.buildWarehouse();
			}

			this.scene.add(this.siloGroup);
		},

		disposeSilo() {
			if (this.siloGroup && this.scene) this.scene.remove(this.siloGroup);
			(this._disposables || []).forEach(d => { try { d.dispose && d.dispose(); } catch (_) { /* noop */ } });
			this._disposables = [];
			this._labelCache = new Map();
			this.arcoGroups = {};
			this.caboCentralGroup = null;
			this.nivelGroup = null;
			this.siloGroup = null;
		},

		// ---------------------------------------------------------------
		// Silo cilíndrico
		// ---------------------------------------------------------------
		buildSiloTank() {
			const g = this.siloGroup;

			// Piso (diâmetro um pouco maior que o silo).
			const floor = new THREE.Mesh(this.track(new THREE.CylinderGeometry(R + 0.18, R + 0.18, 0.08, 48)), this._floorMat);
			floor.position.y = -1.04;
			g.add(floor);

			// Corpo corrugado (Lathe).
			const yBot = -1, bodyH = 2, aneis = 160, ondas = 16, amp = 0.028;
			const profile = [];
			for (let i = 0; i <= aneis; i++) {
				const t = i / aneis;
				profile.push(new THREE.Vector2(R + amp * Math.sin(t * Math.PI * 2 * ondas), yBot + t * bodyH));
			}
			const body = new THREE.Mesh(this.track(new THREE.LatheGeometry(profile, 64)), this._shellMat);
			body.renderOrder = 2;
			g.add(body);

			// Telhado com painéis.
			const roofH = 0.55, roofSeg = 22;
			const roof = new THREE.Mesh(this.track(new THREE.ConeGeometry(R + 0.015, roofH, roofSeg, 1, true)), this._roofMat);
			roof.position.y = 1 + roofH / 2;
			roof.renderOrder = 2;
			g.add(roof);

			// Nervuras radiais do telhado.
			const apex = new THREE.Vector3(0, 1 + roofH, 0);
			const ribPts = [];
			for (let i = 0; i < roofSeg; i++) {
				const a = (i / roofSeg) * Math.PI * 2;
				ribPts.push(apex.clone(), new THREE.Vector3(R * Math.cos(a), 1, R * Math.sin(a)));
			}
			g.add(new THREE.LineSegments(this.track(new THREE.BufferGeometry().setFromPoints(ribPts)), this._lineMat));

			// Anel de beiral (translúcido) e base.
			const eave = new THREE.Mesh(this.track(new THREE.TorusGeometry(R + 0.01, 0.03, 8, 64)), this._eaveMat);
			eave.rotation.x = Math.PI / 2; eave.position.y = 1; g.add(eave);
			const base = new THREE.Mesh(this.track(new THREE.TorusGeometry(R + 0.01, 0.035, 8, 64)), this._bandMat);
			base.rotation.x = Math.PI / 2; base.position.y = -1; g.add(base);

			// Anéis de corrugação.
			[-1, -0.5, 0, 0.5, 1].forEach(y => g.add(this.makeRing(R, y, 64)));

			// Escada lateral alinhada ao cabo 1 (levemente à esquerda).
			this.buildLadder(g);

			// Grão.
			this.buildGrain();

			// Cabos + sensores por arco.
			const nArcos = this.arcosExistentes.length;
			this.arcosExistentes.forEach((arco, index) => {
				const cabos = this.silo.cabos
					.filter(c => c.arco === arco)
					.sort((a, b) => (a.posicao ?? 0) - (b.posicao ?? 0));
				const caboPorArco = cabos.length;
				const raioArco = (RAIO_ARCO_MAP[nArcos] && RAIO_ARCO_MAP[nArcos][index]) ?? R * 0.5;

				const arcoGroup = new THREE.Group();
				arcoGroup.name = `arco-${arco}`;
				const beadPos = [];
				const beadCol = [];

				for (let i = 0; i < caboPorArco; i++) {
					const cabo = cabos[caboPorArco - 1 - i];
					const dados = (this.termometria?.dados || []).filter(d => d.cabo === cabo.cabo);
					if (dados.length !== 1) continue;
					const dado = dados[0];

					const angle = (i / caboPorArco) * Math.PI * 2;
					const x = raioArco * Math.cos(angle);
					const z = raioArco * Math.sin(angle);
					const topY = this.roofYAt(raioArco);
					const botY = -1;
					this.addCable(arcoGroup, x, z, topY, botY);

					const sensores = Math.max(1, cabo.sensores || 0);
					const comprimento = 2.47 - raioArco * 0.5;
					const y0 = botY + MARGEM_CABO;
					const y1 = botY + comprimento - MARGEM_CABO;
					for (let j = 0; j < sensores; j++) {
						const y = sensores > 1 ? y0 + ((y1 - y0) / (sensores - 1)) * j : (y0 + y1) / 2;
						const temp = dado.temperaturas ? dado.temperaturas[`s${j + 1}`] : null;
						beadPos.push(new THREE.Vector3(x, y, z));
						beadCol.push(new THREE.Color(this.corPorTemp(temp)));
						arcoGroup.add(this.makeLabel(this.tempLabel(temp), new THREE.Vector3(x, y + 0.02, z), { size: 0.11, onTop: true }));
					}
					arcoGroup.add(this.makeLabel(String(dado.cabo), new THREE.Vector3(x, topY + 0.05, z), { size: 0.15, color: COR_CABO }));
				}

				const beads = this.makeBeads(beadPos, beadCol);
				if (beads) arcoGroup.add(beads);
				arcoGroup.visible = this.arcosVisiveis[arco] !== false;
				this.arcoGroups[arco] = arcoGroup;
				g.add(arcoGroup);
			});

			this.buildCaboCentral();
		},

		buildLadder(group) {
			const ladder = new THREE.Group();
			const railGeo = this.track(new THREE.CylinderGeometry(0.01, 0.01, 2, 5));
			const rungGeo = this.track(new THREE.CylinderGeometry(0.008, 0.008, 0.18, 5));
			const rL = new THREE.Mesh(railGeo, this._ladderMat); rL.position.set(-0.09, 0, 0);
			const rR = new THREE.Mesh(railGeo, this._ladderMat); rR.position.set(0.09, 0, 0);
			ladder.add(rL, rR);
			for (let i = 0; i < 9; i++) {
				const rung = new THREE.Mesh(rungGeo, this._ladderMat);
				rung.rotation.z = Math.PI / 2;
				rung.position.set(0, -0.85 + i * 0.21, 0);
				ladder.add(rung);
			}
			const a1 = this.anguloDoCabo(1) + 0.16; // levemente à esquerda do cabo 1
			ladder.position.set((R + 0.05) * Math.cos(a1), 0, (R + 0.05) * Math.sin(a1));
			ladder.rotation.y = Math.PI / 2 - a1;
			group.add(ladder);
		},

		buildGrain() {
			const pct = (this.silo.tipo_nivel == 1 ? this.silo.nivel_automatico : this.silo.nivel_manual);
			const found = NIVEL_MAP.find(n => pct < n.max) || NIVEL_MAP[NIVEL_MAP.length - 1];
			const mapped = found.nivel;
			const frac = mapped / 2;
			const nivel = Math.max(0.05, mapped * 0.95); // no 100% o corpo para logo abaixo do beiral
			const varianivel = -1 + nivel / 2;
			const rr = R - 0.03;
			const temTopo = mapped >= 0.05;

			const grp = new THREE.Group();
			// Grão sem iluminação (MeshBasic) para não escurecer ao olhar por baixo.
			const grainMat = this.track(new THREE.MeshBasicMaterial({
				vertexColors: true,
				transparent: true, opacity: 0.62, side: THREE.DoubleSide, depthWrite: false
			}));

			const bodyGeo = this.track(new THREE.CylinderGeometry(rr, rr, nivel, 48, 1, true));
			this.paintByHeight(bodyGeo, -nivel / 2, nivel / 2);
			const body = new THREE.Mesh(bodyGeo, grainMat);
			body.position.y = varianivel; body.renderOrder = 1;
			grp.add(body);

			if (temTopo) {
				// Pilha abaulada que cresce com o preenchimento e entra no chapéu no 100%.
				const domeH = 0.16 + 0.22 * frac;
				const domeGeo = this.track(new THREE.ConeGeometry(rr, domeH, 48, 1, false));
				this.paintByHeight(domeGeo, -domeH / 2, domeH / 2);
				const dome = new THREE.Mesh(domeGeo, grainMat);
				dome.position.y = varianivel + nivel / 2 + domeH / 2 - 0.01; dome.renderOrder = 1;
				grp.add(dome);
			}

			grp.visible = this.nivelVisivel;
			this.nivelGroup = grp;
			this.siloGroup.add(grp);
		},

		buildCaboCentral() {
			const dados = this.termometria?.dados || [];
			const possui = this.silo.cabos.some(c => c.arco === 999);
			if (!possui || dados.length === 0) {
				this.caboCentralGroup = null;
				return;
			}
			const last = dados[dados.length - 1];
			const sensores = Math.max(1, last.sensores || 0);

			const grp = new THREE.Group();
			const topY = 1.6;
			const botY = -1;
			this.addCable(grp, 0, 0, topY, botY);

			const beadPos = [];
			const beadCol = [];
			const y0 = botY + MARGEM_CABO;
			const y1 = botY + 2.5 - MARGEM_CABO;
			for (let i = 0; i < sensores; i++) {
				const y = sensores > 1 ? y0 + ((y1 - y0) / (sensores - 1)) * i : (y0 + y1) / 2;
				const temp = last.temperaturas ? last.temperaturas[`s${i + 1}`] : null;
				beadPos.push(new THREE.Vector3(0, y, 0));
				beadCol.push(new THREE.Color(this.corPorTemp(temp)));
				grp.add(this.makeLabel(this.tempLabel(temp), new THREE.Vector3(0.02, y + 0.02, 0.02), { size: 0.11, onTop: true }));
			}
			const beads = this.makeBeads(beadPos, beadCol);
			if (beads) grp.add(beads);

			// Número do cabo central = maior número de cabo + 1.
			const centralNumero = Math.max(0, ...this.silo.cabos.filter(c => c.arco !== 999).map(c => c.cabo)) + 1;
			grp.add(this.makeLabel(String(centralNumero), new THREE.Vector3(0, 1.65, 0), { size: 0.15, color: COR_CABO }));

			grp.visible = this.caboCentralVisivel;
			this.caboCentralGroup = grp;
			this.siloGroup.add(grp);
		},

		// ---------------------------------------------------------------
		// Armazém
		// ---------------------------------------------------------------
		buildWarehouse() {
			const g = this.siloGroup;
			const nArcos = this.arcosExistentes.length;
			const escala = 1.7;
			const largura = 3.5 * escala;
			const altura = 0.9 * escala;
			const profundidade = Math.max(1, nArcos * 0.5) * escala;
			const yFloor = -1;
			const hL = largura / 2;
			const hP = profundidade / 2;

			// Piso.
			const floor = new THREE.Mesh(this.track(new THREE.BoxGeometry(largura + 0.3, 0.08, profundidade + 0.3)), this._floorMat);
			floor.position.y = yFloor - 0.04;
			g.add(floor);

			// Corpo.
			const box = new THREE.Mesh(this.track(new THREE.BoxGeometry(largura, altura, profundidade)), this._shellMat);
			box.position.y = yFloor + altura / 2;
			box.renderOrder = 2;
			g.add(box);
			const boxEdges = new THREE.LineSegments(this.track(new THREE.EdgesGeometry(box.geometry)), this._lineMat);
			boxEdges.position.copy(box.position);
			g.add(boxEdges);

			// Telhado de duas águas.
			const anguloBase = Math.PI / 6;
			const telhadoAltura = hL * Math.tan(anguloBase);
			const yBase = box.position.y + altura / 2;
			const v1 = [-hL, yBase, -hP], v2 = [hL, yBase, -hP], v3 = [hL, yBase, hP], v4 = [-hL, yBase, hP];
			const v5 = [0, yBase + telhadoAltura, -hP], v6 = [0, yBase + telhadoAltura, hP];

			const verts = [].concat(v1, v4, v6, v1, v6, v5, v2, v5, v6, v2, v6, v3);
			const telhadoGeometry = this.track(new THREE.BufferGeometry());
			telhadoGeometry.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
			telhadoGeometry.computeVertexNormals();
			g.add(new THREE.Mesh(telhadoGeometry, this._roofMat));

			const frenteGeo = this.track(new THREE.BufferGeometry());
			frenteGeo.setAttribute('position', new THREE.Float32BufferAttribute([-hL, yBase, -hP, hL, yBase, -hP, 0, yBase + telhadoAltura, -hP], 3));
			frenteGeo.computeVertexNormals();
			g.add(new THREE.Mesh(frenteGeo, this._roofMat));

			const trasGeo = this.track(new THREE.BufferGeometry());
			trasGeo.setAttribute('position', new THREE.Float32BufferAttribute([-hL, yBase, hP, hL, yBase, hP, 0, yBase + telhadoAltura, hP], 3));
			trasGeo.computeVertexNormals();
			g.add(new THREE.Mesh(trasGeo, this._roofMat));

			const roofLines = [].concat(
				[0, yBase + telhadoAltura, -hP], [0, yBase + telhadoAltura, hP],
				[-hL, yBase, -hP], [-hL, yBase, hP],
				[hL, yBase, -hP], [hL, yBase, hP],
				[-hL, yBase, -hP], [0, yBase + telhadoAltura, -hP],
				[hL, yBase, -hP], [0, yBase + telhadoAltura, -hP],
				[-hL, yBase, hP], [0, yBase + telhadoAltura, hP],
				[hL, yBase, hP], [0, yBase + telhadoAltura, hP]
			);
			const roofLineGeo = this.track(new THREE.BufferGeometry());
			roofLineGeo.setAttribute('position', new THREE.Float32BufferAttribute(roofLines, 3));
			g.add(new THREE.LineSegments(roofLineGeo, this._lineMat));

			// Cabos em fileiras.
			const fileiraEspaco = profundidade / (nArcos + 1);
			const maxSens = Math.max(1, ...this.silo.cabos.map(c => c.sensores || 1));
			const numPos = 9;
			const posX = Array.from({ length: numPos }, (_, i) => -hL + ((i + 1) * largura) / (numPos + 1));

			this.arcosExistentes.forEach((arco, idx) => {
				const z = -hP + fileiraEspaco * (idx + 1);
				const cabos = this.silo.cabos.filter(c => c.arco === arco);
				const arcoGroup = new THREE.Group();
				arcoGroup.name = `arco-${arco}`;
				const beadPos = [];
				const beadCol = [];

				cabos.forEach(cabo => {
					const dados = (this.termometria?.dados || []).filter(d => d.cabo === cabo.cabo);
					if (dados.length !== 1) return;
					const dado = dados[0];
					const pos = parseInt(cabo.posicao);
					if (Number.isNaN(pos) || pos < 1 || pos > 9) return;

					const x = posX[pos - 1];
					const roofUnderY = yBase + telhadoAltura * (1 - Math.abs(x) / hL) - 0.05;
					const botY = yFloor + 0.02;
					this.addCable(arcoGroup, x, z, roofUnderY, botY);

					const sensores = Math.max(1, cabo.sensores || 0);
					const comprimento = (roofUnderY - botY) * (sensores / maxSens);
					const y0 = botY + MARGEM_CABO;
					const y1 = Math.max(y0, botY + comprimento - MARGEM_CABO);
					for (let j = 0; j < sensores; j++) {
						const y = sensores > 1 ? y0 + ((y1 - y0) / (sensores - 1)) * j : (y0 + y1) / 2;
						const temp = dado.temperaturas ? dado.temperaturas[`s${j + 1}`] : null;
						beadPos.push(new THREE.Vector3(x, y, z));
						beadCol.push(new THREE.Color(this.corPorTemp(temp)));
						arcoGroup.add(this.makeLabel(this.tempLabel(temp), new THREE.Vector3(x, y + 0.02, z), { size: 0.1, onTop: true }));
					}
					arcoGroup.add(this.makeLabel(String(dado.cabo), new THREE.Vector3(x, roofUnderY + 0.08, z), { size: 0.13, color: COR_CABO }));
				});

				const beads = this.makeBeads(beadPos, beadCol);
				if (beads) arcoGroup.add(beads);
				arcoGroup.visible = this.arcosVisiveis[arco] !== false;
				this.arcoGroups[arco] = arcoGroup;
				g.add(arcoGroup);
			});
		},

		// ---------------------------------------------------------------
		// Helpers
		// ---------------------------------------------------------------
		roofYAt(raio) {
			return 1 + 0.55 * (1 - Math.min(raio, R) / R);
		},

		anguloDoCabo(numero) {
			const arcoCabos = (this.silo.cabos || []).filter(c => c.arco !== 999);
			if (!arcoCabos.length) return 0;
			let alvo = arcoCabos.find(c => c.cabo === numero);
			if (!alvo) alvo = arcoCabos.reduce((a, b) => (a.cabo <= b.cabo ? a : b));
			const list = arcoCabos.filter(c => c.arco === alvo.arco).sort((a, b) => (a.posicao ?? 0) - (b.posicao ?? 0));
			const k = list.findIndex(c => c.cabo === alvo.cabo);
			const caboPorArco = list.length;
			const i = caboPorArco - 1 - k;
			return (i / caboPorArco) * Math.PI * 2;
		},

		makeRing(raio, y, seg) {
			const pts = [];
			for (let i = 0; i <= seg; i++) {
				const a = (i / seg) * Math.PI * 2;
				pts.push(new THREE.Vector3(raio * Math.cos(a), y, raio * Math.sin(a)));
			}
			const geo = this.track(new THREE.BufferGeometry().setFromPoints(pts));
			return new THREE.LineLoop(geo, this._lineMat);
		},

		// Cabo = linha 2D sem iluminação.
		addCable(group, x, z, topY, botY) {
			const geo = this.track(new THREE.BufferGeometry().setFromPoints([
				new THREE.Vector3(x, botY, z),
				new THREE.Vector3(x, topY, z)
			]));
			group.add(new THREE.Line(geo, this._cableMat));
		},

		// Textura de círculo para o disco do sensor.
		makeCircleTexture() {
			const s = 64;
			const cv = document.createElement('canvas');
			cv.width = cv.height = s;
			const ctx = cv.getContext('2d');
			ctx.beginPath();
			ctx.arc(s / 2, s / 2, s / 2 - 4, 0, Math.PI * 2);
			ctx.fillStyle = '#fff';
			ctx.fill();
			ctx.lineWidth = 4;
			ctx.strokeStyle = 'rgba(20,25,32,0.55)';
			ctx.stroke();
			const tex = new THREE.CanvasTexture(cv);
			tex.minFilter = THREE.LinearFilter;
			tex.generateMipmaps = false;
			return tex;
		},

		// Sensores = discos 2D (billboards) sem iluminação, por cima de tudo.
		makeBeads(positions, colors) {
			if (!positions.length) return null;
			const pos = new Float32Array(positions.length * 3);
			const col = new Float32Array(positions.length * 3);
			for (let i = 0; i < positions.length; i++) {
				pos[i * 3] = positions[i].x; pos[i * 3 + 1] = positions[i].y; pos[i * 3 + 2] = positions[i].z;
				col[i * 3] = colors[i].r; col[i * 3 + 1] = colors[i].g; col[i * 3 + 2] = colors[i].b;
			}
			const geo = this.track(new THREE.BufferGeometry());
			geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
			geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
			const pts = new THREE.Points(geo, this._beadMat);
			pts.frustumCulled = false;
			pts.renderOrder = 15;
			return pts;
		},

		makeLabel(text, position, opts = {}) {
			const size = opts.size ?? 0.12;
			const color = opts.color ?? '#ffffff';
			const onTop = opts.onTop === true;
			const stroke = 'rgba(15,20,28,0.9)';
			const weight = '700';
			const key = `${text}|${color}|${onTop}`;

			let cached = this._labelCache.get(key);
			if (!cached) {
				const fontPx = 72;
				const pad = 16;
				const measure = document.createElement('canvas').getContext('2d');
				measure.font = `${weight} ${fontPx}px Arial, sans-serif`;
				const tw = Math.max(1, Math.ceil(measure.measureText(text).width));
				const cw = tw + pad * 2;
				const ch = fontPx + pad * 2;

				const canvas = document.createElement('canvas');
				canvas.width = cw;
				canvas.height = ch;
				const ctx = canvas.getContext('2d');
				ctx.font = `${weight} ${fontPx}px Arial, sans-serif`;
				ctx.textAlign = 'center';
				ctx.textBaseline = 'middle';
				ctx.lineJoin = 'round';
				ctx.lineWidth = 10;
				ctx.strokeStyle = stroke;
				ctx.strokeText(text, cw / 2, ch / 2);
				ctx.fillStyle = color;
				ctx.fillText(text, cw / 2, ch / 2);

				const tex = new THREE.CanvasTexture(canvas);
				tex.minFilter = THREE.LinearFilter;
				tex.magFilter = THREE.LinearFilter;
				tex.generateMipmaps = false;
				const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: !onTop, depthWrite: false });
				this._disposables.push(tex, mat);
				cached = { mat, aspect: cw / ch };
				this._labelCache.set(key, cached);
			}

			const sprite = new THREE.Sprite(cached.mat);
			sprite.scale.set(size * cached.aspect, size, 1);
			sprite.center.set(0.5, 0);
			sprite.position.copy(position);
			sprite.renderOrder = onTop ? 20 : 10;
			return sprite;
		},

		paintByHeight(geo, yMin, yMax) {
			const cBottom = new THREE.Color(GRAO_BAIXO);
			const cTop = new THREE.Color(GRAO_TOPO);
			const pos = geo.attributes.position;
			const col = [];
			for (let i = 0; i < pos.count; i++) {
				const t = THREE.MathUtils.clamp((pos.getY(i) - yMin) / Math.max(0.001, yMax - yMin), 0, 1);
				const c = cBottom.clone().lerp(cTop, t);
				col.push(c.r, c.g, c.b);
			}
			geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
		},

		// ---------------------------------------------------------------
		// Cores / labels de temperatura
		// ---------------------------------------------------------------
		corPorTemp(temp) {
			const t = Number(temp);
			if (Number.isNaN(t)) return '#ffffff';
			const faixas = Object.keys(temperaturaCores).map(Number).sort((a, b) => a - b);
			for (const f of faixas) {
				if (t <= f) return temperaturaCores[f];
			}
			return '#ffffff';
		},

		tempLabel(temp) {
			const t = Number(temp);
			if (Number.isNaN(t)) return '--';
			return t < 99 ? String(temp) : 'OFF';
		},

		// ---------------------------------------------------------------
		// Toggles / reset
		// ---------------------------------------------------------------
		toggleArco(arco) {
			const grp = this.arcoGroups[arco];
			if (grp) {
				grp.visible = this.arcosVisiveis[arco];
				this.requestRender();
			}
		},
		toggleCaboCentral() {
			if (this.caboCentralGroup) {
				this.caboCentralGroup.visible = this.caboCentralVisivel;
				this.requestRender();
			}
		},
		toggleNivel() {
			if (this.nivelGroup) {
				this.nivelGroup.visible = this.nivelVisivel;
				this.requestRender();
			}
		},
		resetSilo() {
			this.camera.position.set(4.4, 2.5, 5.8);
			this.controls.target.set(0, 0.15, 0);
			this.controls.update();
			this.requestRender();
		}
	}
};
</script>

<style scoped>
#scene {
	display: block;
	height: 60vh;
	width: 100%;
	min-height: 320px;
	cursor: grab;
}

#scene:active {
	cursor: grabbing;
}
</style>
